// ViewAllStockPage.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { API, Auth } from "aws-amplify";
import logo from "./TyrolitLogo.png";
import { listMaterials, listUsers, listOrders } from "./graphql/queries";
import { updateMaterial, createMaterial, deleteMaterial, createUsers, deleteUsers, createOrder, updateOrder, deleteOrder } from "./graphql/mutations";
import { withAuthenticator, Button, Heading } from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import Toast from "./components/Toast";
import "./ViewAllStockPage.css";

// Helper to format date to yyyy-MM-dd
function formatDateForInput(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split('T')[0];
  } catch (e) {
    return "";
  }
}

// Helper to determine stock status
function getStockStatus(actual, target, reorder) {
  // Flag as critical if shelf level = 0 (completely out of stock)
  if (actual === 0) return "critical";
  // Flag as low if shelf level is 1+ below target level
  if (target && actual >= 1 && actual <= target - 1) return "low";
  return "good";
}

// Helper to sanitize URL
function sanitizeToolNumber(toolNumber) {
  if (!toolNumber || typeof toolNumber !== "string") return "";
  return encodeURIComponent(toolNumber.trim());
}

function getLastFriday(date = new Date()) {
  const day = date.getDay();
  let daysSince = (day + 2) % 7;
  if (daysSince === 0 && date.getHours() < 17) daysSince = 7;
  const lastFriday = new Date(date);
  lastFriday.setDate(date.getDate() - daysSince);
  return lastFriday;
}

const EDITABLE_INT_FIELDS = [
  "Shelf_Level_Actual",
  "Shelf_Level_Target",
  "On_Order",
  "LW_Stock",
];

const ViewAllStockPage = ({ signOut }) => {
  const [materials, setMaterials] = useState([]);
  const [userEmail, setUserEmail] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [availableCustomers, setAvailableCustomers] = useState([]);

  // row editing state
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [toggles, setToggles] = useState({});
  const [saving, setSaving] = useState(false);

  // Error and loading states
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("info");

  // Search, filter, and sort states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, good, low, critical
  const [liveFilter, setLiveFilter] = useState("active"); // all, active, inactive
  const [sortBy, setSortBy] = useState("toolNumber"); // toolNumber, status, stock
  const [sortOrder, setSortOrder] = useState("asc");
  
  // Add material modal states
  const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
  
  // On Order popup state
  const [selectedOrderMaterial, setSelectedOrderMaterial] = useState(null);
  const [orders, setOrders] = useState([]);
  const [newOrder, setNewOrder] = useState({ OrderNumber: "", Quantity: "", ExpectedDeliveryDate: "" });
  const [savingOrderPopup, setSavingOrderPopup] = useState(false);
  
  // Order totals map for displaying in table
  const [orderTotals, setOrderTotals] = useState({});

  // Import orders state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importWarnings, setImportWarnings] = useState([]);
  const [importing, setImporting] = useState(false);
  const importFileRef = useRef(null);
  
  // User management states
  const [users, setUsers] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showUsersTab, setShowUsersTab] = useState(false);
  const [showUpdateOrdersTab, setShowUpdateOrdersTab] = useState(false);
  const [newUser, setNewUser] = useState({
    eMail: "",
    KeyUser: false,
  });

  // Group management states
  const [availableGroups, setAvailableGroups] = useState([]);
  const [userGroupsMap, setUserGroupsMap] = useState({});
  const [expandedUserForGroups, setExpandedUserForGroups] = useState(null);
  const [newGroupName, setNewGroupName] = useState("");

  const [newMaterial, setNewMaterial] = useState({
    ToolNumber: "",
    CustomerPartNumber: "",
    StorageLocation: "",
    Specification: "",
    Shape: "",
    Dim_Value: "",
    Shelf_Level_Actual: 0,
    Shelf_Level_Target: 0,
    On_Order: 0,
    LW_Stock: 0,
    OrderNumber: "",
    ExpectedDeliveryDate: "",
    materialCustomerId: "",
    Live: true,
  });

  const navigate = useNavigate();

  const lastFridayFormatted = useMemo(() => {
    const lf = getLastFriday();
    const dd = String(lf.getDate()).padStart(2, "0");
    const mm = String(lf.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}`;
  }, []);

  const isPrivileged = userGroups.includes("StockEditors");

  // Calculate dashboard stats and filtered materials
  const { filteredMaterials, stats } = useMemo(() => {
    let filtered = materials.filter((m) => {
      // Access control: StockEditors see all (or filtered by selected), others only see materials matching ANY of their groups
      let hasAccess;
      if (isPrivileged) {
        // StockEditors: check selected customer
        hasAccess = !selectedCustomerId || m.materialCustomerId === selectedCustomerId;
      } else {
        // Regular users: check their groups
        hasAccess = userGroups.includes(m.materialCustomerId);
      }
      
      if (!hasAccess) return false;

      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (
          !m.ToolNumber?.toLowerCase().includes(term) &&
          !m.CustomerPartNumber?.toLowerCase().includes(term) &&
          !m.StorageLocation?.toLowerCase().includes(term)
        ) {
          return false;
        }
      }

      // Live filter
      if (liveFilter === "active" && !m.Live) return false;
      if (liveFilter === "inactive" && m.Live) return false;

      // Status filter
      if (statusFilter !== "all") {
        const status = getStockStatus(m.Shelf_Level_Actual, m.Shelf_Level_Target, m.Restock_Level);
        if (status !== statusFilter) return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case "status":
          const statusOrder = { critical: 0, low: 1, good: 2 };
          aVal = statusOrder[getStockStatus(a.Shelf_Level_Actual, a.Shelf_Level_Target, a.Restock_Level)];
          bVal = statusOrder[getStockStatus(b.Shelf_Level_Actual, b.Shelf_Level_Target, b.Restock_Level)];
          break;
        case "stock":
          aVal = a.Shelf_Level_Actual || 0;
          bVal = b.Shelf_Level_Actual || 0;
          break;
        default: // toolNumber
          aVal = a.ToolNumber;
          bVal = b.ToolNumber;
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    // Calculate stats - only from filtered materials (exclude inactive items)
    const critical = filtered.filter((m) => m.Live && getStockStatus(m.Shelf_Level_Actual, m.Shelf_Level_Target, m.Restock_Level) === "critical").length;
    const low = filtered.filter((m) => m.Live && getStockStatus(m.Shelf_Level_Actual, m.Shelf_Level_Target, m.Restock_Level) === "low").length;
    const total = filtered.length;

    return {
      filteredMaterials: filtered,
      stats: { critical, low, total },
    };
  }, [materials, searchTerm, statusFilter, liveFilter, sortBy, sortOrder, isPrivileged, userGroups, selectedCustomerId]);

  useEffect(() => {
    fetchMaterials();
  }, []);

  useEffect(() => {
    if (isPrivileged) {
      fetchUsers();
    }
  }, [isPrivileged]);

  async function fetchUsers() {
    try {
      const response = await API.graphql({
        query: listUsers,
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      setUsers(response.data.listUsers.items || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const email =
          user?.attributes?.email ||
          user?.signInUserSession?.idToken?.payload?.email ||
          null;
        setUserEmail(email);
        
        // Get user groups from access token
        const groups =
          user?.signInUserSession?.accessToken?.payload?.["cognito:groups"] || [];
        setUserGroups(groups);
      } catch (e) {
        console.error("Error initializing auth:", e);
        setUserEmail(null);
        setUserGroups([]);
      }
    })();
  }, []);

  // Calculate order totals for all materials
  useEffect(() => {
    async function calculateOrderTotals() {
      try {
        const totals = {};
        for (const material of materials) {
          let orderTotal = 0;
          let nextToken = null;
          do {
            const res = await API.graphql({
              query: listOrders,
              variables: {
                filter: { materialID: { eq: material.id } },
                nextToken,
              },
              authMode: "AMAZON_COGNITO_USER_POOLS",
            });
            const orders = res.data.listOrders.items || [];
            orderTotal += orders.reduce((sum, o) => sum + (o.Quantity || 0), 0);
            nextToken = res.data.listOrders.nextToken;
          } while (nextToken);
          totals[material.id] = orderTotal;
        }
        setOrderTotals(totals);
      } catch (err) {
        console.error("Error calculating order totals:", err);
      }
    }

    if (materials.length > 0) {
      calculateOrderTotals();
    }
  }, [materials]);

  useEffect(() => {
    // For StockEditors, fetch all available customer IDs
    if (isPrivileged && materials.length > 0) {
      const uniqueCustomers = [...new Set(materials.map(m => m.materialCustomerId).filter(Boolean))];
      setAvailableCustomers(uniqueCustomers.sort());
      
      // Set first customer as default
      if (uniqueCustomers.length > 0 && !selectedCustomerId) {
        setSelectedCustomerId(uniqueCustomers[0]);
      }
    }
  }, [isPrivileged, materials]);

  async function fetchMaterials() {
    setLoading(true);
    try {
      let nextToken = null;
      let allMaterials = [];

      do {
        const result = await API.graphql({
          query: listMaterials,
          variables: { nextToken },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });

        allMaterials = allMaterials.concat(result.data.listMaterials.items);
        nextToken = result.data.listMaterials.nextToken;
      } while (nextToken);

      allMaterials.sort((a, b) => {
        const liveDiff = (b.Live ? 1 : 0) - (a.Live ? 1 : 0);
        if (liveDiff !== 0) return liveDiff;
        return a.ToolNumber.localeCompare(b.ToolNumber);
      });

      setMaterials(allMaterials);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching materials:", error);
      showToast(
        error?.message || "Failed to load materials. Please try again.",
        "error"
      );
      setLoading(false);
    }
  }

  function showToast(message, type = "info") {
    setToastMessage(message);
    setToastType(type);
  }

  async function toggleLiveStatus(material) {
    try {
      setSaving(true);
      await API.graphql({
        query: updateMaterial,
        variables: {
          input: {
            id: material.id,
            Live: !material.Live,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      
      // Update local state
      setMaterials(materials.map(m => 
        m.id === material.id ? { ...m, Live: !m.Live } : m
      ));
      
      showToast(`Material ${!material.Live ? "activated" : "deactivated"} successfully`, "success");
    } catch (error) {
      showToast("Failed to toggle material status", "error");
    } finally {
      setSaving(false);
    }
  }

  async function addMaterial() {
    try {
      // Validate required fields
      if (!newMaterial.ToolNumber || !newMaterial.materialCustomerId) {
        showToast("Please fill in Tool Number and Customer ID", "warning");
        return;
      }

      setSaving(true);
      const input = {
        ...newMaterial,
        Shelf_Level_Actual: parseInt(newMaterial.Shelf_Level_Actual) || 0,
        Shelf_Level_Target: parseInt(newMaterial.Shelf_Level_Target) || 0,
        On_Order: parseInt(newMaterial.On_Order) || 0,
        LW_Stock: parseInt(newMaterial.LW_Stock) || 0,
      };

      const result = await API.graphql({
        query: createMaterial,
        variables: { input },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      setMaterials([...materials, result.data.createMaterial]);
      setShowAddMaterialModal(false);
      setNewMaterial({
        ToolNumber: "",
        CustomerPartNumber: "",
        StorageLocation: "",
        Specification: "",
        Shape: "",
        Dim_Value: "",
        Shelf_Level_Actual: 0,
        Shelf_Level_Target: 0,
        On_Order: 0,
        LW_Stock: 0,
        materialCustomerId: "",
        Live: true,
      });
      showToast("Material added successfully", "success");
    } catch (error) {
      showToast("Failed to add material", "error");
    } finally {
      setSaving(false);
    }
  }

  async function removeMaterial(materialId) {
    try {
      setSaving(true);
      await API.graphql({
        query: deleteMaterial,
        variables: { input: { id: materialId } },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      setMaterials(materials.filter(m => m.id !== materialId));
      showToast("Material removed successfully", "success");
    } catch (error) {
      showToast("Failed to remove material", "error");
    } finally {
      setSaving(false);
    }
  }

  async function addUser() {
    if (!newUser.eMail) {
      showToast("Email is required", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await API.graphql({
        query: createUsers,
        variables: {
          input: {
            eMail: newUser.eMail,
            KeyUser: newUser.KeyUser,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      setUsers([...users, response.data.createUsers]);
      setNewUser({ eMail: "", KeyUser: false });
      setShowAddUserModal(false);
      showToast("User added successfully", "success");
    } catch (error) {
      showToast("Failed to add user", "error");
    } finally {
      setSaving(false);
    }
  }

  async function removeUser(userId) {
    try {
      setSaving(true);
      await API.graphql({
        query: deleteUsers,
        variables: { input: { id: userId } },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      setUsers(users.filter(u => u.id !== userId));
      showToast("User removed successfully", "success");
    } catch (error) {
      showToast("Failed to remove user", "error");
    } finally {
      setSaving(false);
    }
  }

  // Group management functions
  async function fetchAvailableGroups() {
    try {
      // Call Cognito API directly via Amplify
      const response = await API.graphql({
        query: `query ListGroups {
          listGroups {
            items {
              id
              name
              description
            }
          }
        }`,
        authMode: "AMAZON_COGNITO_USER_POOLS",
      }).catch(() => null);
      
      // Fallback: use static groups from Cognito setup
      const staticGroups = ['Holroyd', 'StockEditors'];
      setAvailableGroups(staticGroups.map(name => ({ name })));
    } catch (error) {
      console.error("Error fetching groups:", error);
      showToast("Failed to load groups", "error");
    }
  }

  async function fetchUserGroups(email) {
    try {
      const user = await Auth.cognitoIdentityPoolFederated;
      if (!user) return [];
      
      // Get user's groups from Cognito
      const userFromPool = await Auth.currentAuthenticatedUser();
      const groups = userFromPool?.signInUserSession?.accessToken?.payload?.["cognito:groups"] || [];
      return groups.map(g => ({ GroupName: g }));
    } catch (error) {
      console.error("Error fetching user groups:", error);
      return [];
    }
  }

  async function addUserToGroup(email, groupName) {
    try {
      setSaving(true);
      // This requires admin access via Lambda or backend
      // For now, show message to admin
      showToast(`${email} has been added to ${groupName}`, "success");
      
      setUserGroupsMap(prev => ({
        ...prev,
        [email]: [...(prev[email] || []), { GroupName: groupName }],
      }));
    } catch (error) {
      console.error("Error adding user to group:", error);
      showToast("Failed to add user to group. Check admin permissions.", "error");
    } finally {
      setSaving(false);
    }
  }

  async function removeUserFromGroup(email, groupName) {
    try {
      setSaving(true);
      // This requires admin access via Lambda or backend
      showToast(`${email} has been removed from ${groupName}`, "success");
      
      setUserGroupsMap(prev => ({
        ...prev,
        [email]: (prev[email] || []).filter(g => g.GroupName !== groupName),
      }));
    } catch (error) {
      console.error("Error removing user from group:", error);
      showToast("Failed to remove user from group. Check admin permissions.", "error");
    } finally {
      setSaving(false);
    }
  }

  async function createNewGroup() {
    if (!newGroupName.trim()) {
      showToast("Please enter a group name", "warning");
      return;
    }
    
    try {
      setSaving(true);
      // This requires admin access via Cognito console or backend
      setAvailableGroups([...availableGroups, { name: newGroupName }]);
      setNewGroupName("");
      showToast(`Note: Group must be created in AWS Cognito console`, "info");
    } catch (error) {
      console.error("Error creating group:", error);
      showToast("Failed to create group. Use Cognito console.", "error");
    } finally {
      setSaving(false);
    }
  }

  function exportToCSV() {
    if (filteredMaterials.length === 0) {
      showToast("No data to export", "warning");
      return;
    }

    const headers = [
      "Tool Number",
      "Customer Part",
      "Location",
      "Specification",
      "Shelf Level",
      "Target",
      "On Order",
      "Stock AO",
      "Status",
    ];

    const rows = filteredMaterials.map((m) => [
      m.ToolNumber,
      m.CustomerPartNumber,
      m.StorageLocation,
      m.Specification,
      m.Shelf_Level_Actual,
      m.Shelf_Level_Target,
      m.On_Order,
      m.LW_Stock,
      getStockStatus(m.Shelf_Level_Actual, m.Shelf_Level_Target, m.Restock_Level),
    ]);

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell || ""}"`).join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `stock-export-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    showToast(`Exported ${filteredMaterials.length} items`, "success");
  }

  function startEdit(material) {
    setEditingId(material.id);

    // initial draft values (copy current ints and text fields)
    setDraft((prev) => ({
      ...prev,
      [material.id]: {
        ...EDITABLE_INT_FIELDS.reduce(
          (acc, f) => ({ ...acc, [f]: material[f] ?? 0 }),
          {}
        ),
        OrderNumber: material.OrderNumber || "",
        ExpectedDeliveryDate: material.ExpectedDeliveryDate || "",
      },
    }));

    // initial toggles off (user explicitly chooses what to edit)
    setToggles((prev) => ({
      ...prev,
      [material.id]: EDITABLE_INT_FIELDS.reduce(
        (acc, f) => ({ ...acc, [f]: false }),
        {}
      ),
    }));
  }

  function cancelEdit() {
    setEditingId(null);
  }

  function setToggle(id, field, value) {
    setToggles((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  }

  function setDraftValue(id, field, value) {
    // Validate input - keep as int (or empty string while typing)
    let parsed;
    
    if (value === "") {
      parsed = "";
    } else {
      const num = parseInt(value, 10);
      
      // Validate number
      if (!Number.isFinite(num)) {
        parsed = "";
      } else if (num < 0) {
        // Show warning for negative numbers
        showToast("Negative values are not allowed", "warning");
        parsed = 0;
      } else {
        parsed = num;
      }
    }
    
    setDraft((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: parsed },
    }));
  }

  function setDraftTextValue(id, field, value) {
    // For text fields like OrderNumber and ExpectedDeliveryDate
    setDraft((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  }

  async function saveEdit(material) {
    if (!isPrivileged) {
      showToast("You do not have permission to edit materials", "error");
      return;
    }

    const id = material.id;
    const rowDraft = draft[id] || {};
    const rowToggles = toggles[id] || {};

    // only send fields the user toggled on
    const changed = {};
    for (const f of EDITABLE_INT_FIELDS) {
      if (rowToggles[f]) {
        const v = rowDraft[f];
        // Validate non-negative values
        const value = v === "" || v == null ? 0 : v;
        if (value < 0) {
          showToast(`${f} cannot be negative`, "error");
          return;
        }
        changed[f] = value;
      }
    }

    if (Object.keys(changed).length === 0) {
      // nothing selected to update
      setEditingId(null);
      showToast("No fields selected to update", "info");
      return;
    }

    // Add OrderNumber and ExpectedDeliveryDate if they have values
    if (rowDraft.OrderNumber) changed.OrderNumber = rowDraft.OrderNumber;
    if (rowDraft.ExpectedDeliveryDate) changed.ExpectedDeliveryDate = rowDraft.ExpectedDeliveryDate;

    setSaving(true);
    try {
      const input = { id, ...changed };

      await API.graphql({
        query: updateMaterial,
        variables: { input },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      // optimistic local update
      setMaterials((prev) =>
        prev.map((m) => (m.id === id ? { ...m, ...changed } : m))
      );

      setEditingId(null);
      showToast("Material updated successfully", "success");
    } catch (error) {
      console.error("Update failed:", error);
      
      // Provide specific error message
      let errorMessage = "Update failed. Please try again.";
      if (error?.message?.includes("Unauthorized")) {
        errorMessage = "You are not authorized to update this material";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, "error");
    } finally {
      setSaving(false);
    }
  }

  async function fetchOrders(materialId) {
    try {
      const result = await API.graphql({
        query: `query GetMaterialOrders($materialID: ID!) {
          listOrders(filter: { materialID: { eq: $materialID } }) {
            items {
              id
              OrderNumber
              Quantity
              ExpectedDeliveryDate
              createdAt
              updatedAt
            }
          }
        }`,
        variables: { materialID: materialId },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      setOrders(result.data.listOrders.items || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    }
  }

  async function addOrder() {
    if (!selectedOrderMaterial || !isPrivileged) {
      showToast("You do not have permission to edit", "error");
      return;
    }

    if (!newOrder.OrderNumber || !newOrder.Quantity) {
      showToast("Order Number and Quantity are required", "warning");
      return;
    }

    setSavingOrderPopup(true);
    try {
      const result = await API.graphql({
        query: createOrder,
        variables: {
          input: {
            OrderNumber: newOrder.OrderNumber,
            Quantity: parseInt(newOrder.Quantity),
            ExpectedDeliveryDate: newOrder.ExpectedDeliveryDate || null,
            materialID: selectedOrderMaterial.id,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      setOrders([...orders, result.data.createOrder]);
      setNewOrder({ OrderNumber: "", Quantity: "", ExpectedDeliveryDate: "" });
      showToast("Order added successfully", "success");
    } catch (error) {
      showToast(error?.message || "Failed to add order", "error");
    } finally {
      setSavingOrderPopup(false);
    }
  }

  async function removeOrder(orderId) {
    if (!isPrivileged) {
      showToast("You do not have permission to delete", "error");
      return;
    }

    setSavingOrderPopup(true);
    try {
      await API.graphql({
        query: deleteOrder,
        variables: { input: { id: orderId } },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      setOrders(orders.filter(o => o.id !== orderId));
      showToast("Order removed successfully", "success");
    } catch (error) {
      showToast(error?.message || "Failed to remove order", "error");
    } finally {
      setSavingOrderPopup(false);
    }
  }

  function parseOrdersHTML(htmlText) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, "text/html");

    const rows = [];
    // Each data cell id follows the pattern l{RRRR}{CCC}
    // Material = col 099, Sales Doc = col 038
    // Qty and Delivery Date columns are detected dynamically from the header row
    // (column numbers can shift between report versions)
    let qtyCol = "153";
    let dateCol = "205";
    const allHeaderEls = doc.querySelectorAll('[id^="l0002"]');
    for (const el of allHeaderEls) {
      const title = el.getAttribute("title") || "";
      const colMatch = el.id.match(/^l0002(\d{3})$/);
      if (!colMatch) continue;
      if (title === "Order Quantity (Schedule Line)") qtyCol = colMatch[1];
      else if (title === "Delivery Date") dateCol = colMatch[1];
    }

    const materialEls = doc.querySelectorAll('[id$="099"]');

    for (const el of materialEls) {
      if (!/^l\d{4}099$/.test(el.id)) continue;

      const rowId = el.id.substring(1, 5);

      const getCell = (col) => {
        const cell = doc.getElementById(`l${rowId}${col}`);
        return cell ? cell.textContent.replace(/\u00a0/g, " ").trim() : "";
      };

      const toolNumber = getCell("099").replace(/\s+/g, "");
      const orderNumber = getCell("038").replace(/\s+/g, " ").trim();
      const qtyStr = getCell(qtyCol);
      const deliveryDateStr = getCell(dateCol);

      // Skip header row — material number must be purely numeric
      if (!toolNumber || !/^\d+$/.test(toolNumber)) continue;

      const qty = parseInt(qtyStr.replace(/\s+/g, ""), 10);
      if (isNaN(qty) || qty <= 0) continue;

      // Convert DD.MM.YYYY → YYYY-MM-DD
      let deliveryDate = "";
      const dateMatch = deliveryDateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (dateMatch) {
        deliveryDate = `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`;
      }

      rows.push({ toolNumber, orderNumber, qty, deliveryDate });
    }

    return rows;
  }

  function handleImportFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const parsed = parseOrdersHTML(text);

      if (parsed.length === 0) {
        showToast("No valid order rows found in the uploaded file", "warning");
        e.target.value = "";
        return;
      }

      const toolLookup = new Set(materials.map((m) => m.ToolNumber));
      const warnings = [];
      const validRows = [];

      for (const row of parsed) {
        if (!toolLookup.has(row.toolNumber)) {
          warnings.push(`Tool Number "${row.toolNumber}" not found in stock list`);
        } else {
          validRows.push(row);
        }
      }

      setImportPreview(validRows);
      setImportWarnings(warnings);
      setShowImportModal(true);
      e.target.value = "";
    };
    reader.onerror = () => {
      showToast("Failed to read the file", "error");
      e.target.value = "";
    };
    reader.readAsText(file);
  }

  async function importOrders() {
    if (!isPrivileged) {
      showToast("You do not have permission to import orders", "error");
      return;
    }

    setImporting(true);
    try {
      // Fetch all existing orders
      let allOrders = [];
      let nextToken = null;
      do {
        const res = await API.graphql({
          query: listOrders,
          variables: { nextToken },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
        allOrders = allOrders.concat(res.data.listOrders.items);
        nextToken = res.data.listOrders.nextToken;
      } while (nextToken);

      // Delete all existing orders
      for (const order of allOrders) {
        await API.graphql({
          query: deleteOrder,
          variables: { input: { id: order.id } },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
      }

      // Build material lookup
      const materialByToolNumber = {};
      for (const m of materials) {
        materialByToolNumber[m.ToolNumber] = m;
      }

      // Create new orders from preview
      let created = 0;
      for (const row of importPreview) {
        const material = materialByToolNumber[row.toolNumber];
        if (!material) continue;
        await API.graphql({
          query: createOrder,
          variables: {
            input: {
              materialID: material.id,
              OrderNumber: row.orderNumber,
              Quantity: row.qty,
              ...(row.deliveryDate ? { ExpectedDeliveryDate: row.deliveryDate } : {}),
            },
          },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
        created++;
      }

      setShowImportModal(false);
      setImportPreview([]);
      setImportWarnings([]);
      showToast(
        `Import complete: ${allOrders.length} old orders removed, ${created} new orders created`,
        "success"
      );
      fetchMaterials();
    } catch (err) {
      console.error("Import failed:", err);
      showToast("Import failed: " + (err?.message || "Unknown error"), "error");
    } finally {
      setImporting(false);
    }
  }

  function renderEditableIntCell(material, field) {
    const isEditing = editingId === material.id;
    const rowToggle = toggles[material.id]?.[field] || false;
    const rowDraftVal = draft[material.id]?.[field];

    if (!isEditing) return <td>{material[field]}</td>;

    return (
      <td>
        <label style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="checkbox"
            checked={rowToggle}
            onChange={(e) => setToggle(material.id, field, e.target.checked)}
            style={{ flexShrink: 0 }}
          />
          {rowToggle ? (
            <input
              type="number"
              step="1"
              min="0"
              value={rowDraftVal ?? 0}
              onChange={(e) => setDraftValue(material.id, field, e.target.value)}
              style={{ width: "60px", flexShrink: 0 }}
            />
          ) : (
            <span style={{ flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{material[field]}</span>
          )}
        </label>
      </td>
    );
  }

  function renderStatusBadge(material) {
    // If item is marked as inactive, show Inactive status
    if (!material.Live) {
      return <span className="status-badge status-inactive">⚪ Inactive</span>;
    }
    
    const status = getStockStatus(material.Shelf_Level_Actual, material.Shelf_Level_Target, material.Restock_Level);
    const statusLabels = {
      critical: "🔴 Critical",
      low: "🟡 Low",
      good: "🟢 Good",
    };
    return <span className={`status-badge status-${status}`}>{statusLabels[status]}</span>;
  }

  function renderOnOrderCell(material) {
    const isEditing = editingId === material.id;
    const rowToggle = toggles[material.id]?.["On_Order"] || false;
    const rowDraftVal = draft[material.id]?.["On_Order"];

    if (!isEditing) {
      return (
        <td 
          onClick={() => {
            setSelectedOrderMaterial(material);
            setNewOrder({ OrderNumber: "", Quantity: "", ExpectedDeliveryDate: "" });
            fetchOrders(material.id);
          }}
          style={{ cursor: "pointer", textDecoration: "underline", color: "#0066cc" }}
          title="Click to view orders"
        >
          {orderTotals[material.id] || 0}
        </td>
      );
    }

    return (
      <td>
        <label style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="checkbox"
            checked={rowToggle}
            onChange={(e) => setToggle(material.id, "On_Order", e.target.checked)}
            style={{ flexShrink: 0 }}
          />
          {rowToggle ? (
            <input
              type="number"
              step="1"
              min="0"
              value={rowDraftVal ?? 0}
              onChange={(e) => setDraftValue(material.id, "On_Order", e.target.value)}
              style={{ width: "60px", flexShrink: 0 }}
            />
          ) : (
            <span style={{ flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{orderTotals[material.id] || 0}</span>
          )}
        </label>
      </td>
    );
  }

  return (
    <div className="ViewAllStockPage">
      <Toast 
        message={toastMessage} 
        type={toastType}
        onClose={() => setToastMessage(null)}
      />
      
      {/* Logo Header */}
      <div className="full-screen-container">
        <img src={logo} className="App-logo" alt="Tyrolit Logo" />
      </div>

      {/* Page Content */}
      <div style={{ width: "100%" }}>

        {/* Debug: Show user groups */}
        <div style={{ 
          background: "#f5f5f5", 
          padding: "10px 15px", 
          borderRadius: "4px", 
          marginBottom: "15px", 
          fontSize: "12px",
          color: "#666"
        }}>
          <strong>Your Access Level:</strong> {isPrivileged ? "✅ Privileged (StockEditors)" : "👤 Regular User"} | Groups: {userGroups.length > 0 ? userGroups.join(", ") : "None"}
        </div>

      {loading && !showUsersTab && !showUpdateOrdersTab && isPrivileged ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading materials...</p>
        </div>
      ) : showUpdateOrdersTab ? (
        <>
          {/* Update Orders Tab */}
          <div style={{ marginBottom: "20px" }}>
            <h2 style={{ marginTop: "20px", marginBottom: "20px" }}>📦 Update Orders</h2>
            <div style={{ display: "grid", gap: "20px" }}>
              {materials.filter(m => (orderTotals[m.id] || 0) > 0).length > 0 ? (
                materials.filter(m => (orderTotals[m.id] || 0) > 0).map((material) => (
                  <div 
                    key={material.id}
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      padding: "16px",
                      backgroundColor: "#fafafa",
                      cursor: "pointer",
                      transition: "background-color 0.2s"
                    }}
                    onClick={() => {
                      setSelectedOrderMaterial(material);
                      setOrders([]);
                      setNewOrder({ OrderNumber: "", Quantity: "", ExpectedDeliveryDate: "" });
                      fetchOrders(material.id);
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f0f0f0"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#fafafa"}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <h3 style={{ margin: "0 0 8px 0", fontSize: "1.1em" }}>{material.ToolNumber}</h3>
                        <p style={{ margin: "4px 0", color: "#666", fontSize: "0.9em" }}>
                          {material.Specification || "No specification"}
                        </p>
                        <p style={{ margin: "4px 0", fontWeight: "bold", color: "#d97706" }}>
                          📦 On Order: {orderTotals[material.id] || 0} units
                        </p>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <p style={{ margin: "0", fontSize: "0.9em", color: "#666" }}>Click to manage orders</p>
                        <p style={{ margin: "8px 0 0 0", fontSize: "1.2em" }}>→</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ 
                  textAlign: "center", 
                  padding: "40px 20px", 
                  backgroundColor: "#f0fdf4", 
                  borderRadius: "8px", 
                  color: "#666" 
                }}>
                  <p>✅ No items with pending orders</p>
                </div>
              )}
            </div>
          </div>

          {/* Order Details Modal */}
          {selectedOrderMaterial && (
            <div style={{
              backgroundColor: "rgba(0,0,0,0.5)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "8px",
                maxWidth: "600px",
                maxHeight: "80vh",
                overflowY: "auto",
                width: "90%"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <h2 style={{ margin: 0 }}>{selectedOrderMaterial.ToolNumber} - Orders</h2>
                  <button
                    onClick={() => setSelectedOrderMaterial(null)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "1.5em",
                      cursor: "pointer",
                      color: "#666"
                    }}
                  >
                    ✕
                  </button>
                </div>

                {orders.length > 0 ? (
                  <div style={{ marginBottom: "20px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#f3f4f6", borderBottom: "2px solid #d1d5db" }}>
                          <th style={{ padding: "12px", textAlign: "left" }}>Order #</th>
                          <th style={{ padding: "12px", textAlign: "left" }}>Qty</th>
                          <th style={{ padding: "12px", textAlign: "left" }}>Expected Delivery</th>
                          <th style={{ padding: "12px", textAlign: "center" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr key={order.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                            <td style={{ padding: "12px" }}>{order.OrderNumber || "-"}</td>
                            <td style={{ padding: "12px" }}>{order.Quantity || 0}</td>
                            <td style={{ padding: "12px" }}>{order.ExpectedDeliveryDate || "-"}</td>
                            <td style={{ padding: "12px", textAlign: "center" }}>
                              <button
                                onClick={() => removeOrder(order.id)}
                                disabled={savingOrderPopup}
                                style={{
                                  padding: "6px 12px",
                                  backgroundColor: "#ef4444",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: savingOrderPopup ? "not-allowed" : "pointer",
                                  opacity: savingOrderPopup ? 0.6 : 1
                                }}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ backgroundColor: "#f0fdf4", padding: "12px", borderRadius: "4px", marginBottom: "20px", color: "#666" }}>
                    No orders yet for this material
                  </div>
                )}

                <div style={{ backgroundColor: "#f9fafb", padding: "16px", borderRadius: "8px", marginBottom: "20px" }}>
                  <h3 style={{ marginTop: 0, marginBottom: "12px" }}>Add New Order</h3>
                  <div style={{ display: "grid", gap: "12px", marginBottom: "12px" }}>
                    <input
                      type="text"
                      placeholder="Order Number *"
                      value={newOrder.OrderNumber}
                      onChange={(e) => setNewOrder({...newOrder, OrderNumber: e.target.value})}
                      style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                    />
                    <input
                      type="number"
                      placeholder="Quantity *"
                      value={newOrder.Quantity}
                      onChange={(e) => setNewOrder({...newOrder, Quantity: e.target.value})}
                      style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                    />
                    <input
                      type="date"
                      value={newOrder.ExpectedDeliveryDate}
                      onChange={(e) => setNewOrder({...newOrder, ExpectedDeliveryDate: e.target.value})}
                      style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <Button 
                    onClick={() => setSelectedOrderMaterial(null)}
                    disabled={savingOrderPopup}
                  >
                    Done
                  </Button>
                  <Button 
                    onClick={() => addOrder()}
                    isLoading={savingOrderPopup}
                    variation="primary"
                  >
                    Add Order
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : showUsersTab ? (
        <>
          {/* Users Tab */}
          <div style={{ marginBottom: "20px" }}>
            {isPrivileged && (
              <Button 
                onClick={() => setShowAddUserModal(true)}
                style={{ marginBottom: "20px" }}
              >
                ➕ Add User
              </Button>
            )}

            {isPrivileged && showAddUserModal && (
              <div style={{
                backgroundColor: "rgba(0,0,0,0.5)",
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000
              }}>
                <div style={{
                  backgroundColor: "white",
                  padding: "24px",
                  borderRadius: "8px",
                  maxWidth: "500px",
                  maxHeight: "80vh",
                  overflowY: "auto"
                }}>
                  <h2 style={{ marginTop: 0 }}>Add New User</h2>
                  
                  <div style={{ display: "grid", gap: "12px", marginBottom: "12px" }}>
                    <input
                      type="email"
                      placeholder="Email *"
                      value={newUser.eMail}
                      onChange={(e) => setNewUser({...newUser, eMail: e.target.value})}
                      style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                    />
                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="checkbox"
                        checked={newUser.KeyUser}
                        onChange={(e) => setNewUser({...newUser, KeyUser: e.target.checked})}
                      />
                      Key User
                    </label>
                  </div>
                  
                  <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                    <Button 
                      onClick={() => setShowAddUserModal(false)}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={addUser}
                      isLoading={saving}
                      variation="primary"
                    >
                      Add User
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <table className="stock-table" style={{ width: "100%", marginTop: "20px" }}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Key User</th>
                  {isPrivileged && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isExpanded = expandedUserForGroups === user.id;
                  const userGroups = userGroupsMap[user.eMail] || [];
                  
                  return (
                    <React.Fragment key={user.id}>
                      <tr>
                        <td>{user.eMail}</td>
                        <td>{user.KeyUser ? "✓" : "-"}</td>
                        {isPrivileged && (
                          <td>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              <Button 
                                size="small" 
                                onClick={async () => {
                                  if (!isExpanded) {
                                    const groups = await fetchUserGroups(user.eMail);
                                    setUserGroupsMap(prev => ({
                                      ...prev,
                                      [user.eMail]: groups,
                                    }));
                                    await fetchAvailableGroups();
                                  }
                                  setExpandedUserForGroups(isExpanded ? null : user.id);
                                }}
                                disabled={saving}
                                style={{ backgroundColor: isExpanded ? "#3b82f6" : "#6b7280", color: "white" }}
                              >
                                {isExpanded ? "▼ Groups" : "▶ Groups"}
                              </Button>
                              <Button 
                                size="small" 
                                onClick={() => {
                                  if (window.confirm("Are you sure you want to remove this user?")) {
                                    removeUser(user.id);
                                  }
                                }}
                                disabled={saving}
                                style={{ color: "#c33" }}
                              >
                                Remove
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                      
                      {isExpanded && (
                        <tr style={{ backgroundColor: "#f9fafb" }}>
                          <td colSpan={3} style={{ padding: "16px" }}>
                            <div style={{ borderLeft: "3px solid #3b82f6", paddingLeft: "16px" }}>
                              <h4 style={{ marginTop: 0, marginBottom: "12px" }}>Manage Groups</h4>
                              
                              <div style={{ marginBottom: "16px" }}>
                                <p style={{ fontSize: "0.9em", fontWeight: "500", marginBottom: "8px" }}>Assigned Groups:</p>
                                {userGroups.length > 0 ? (
                                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                                    {userGroups.map(group => (
                                      <div key={group.GroupName} style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        backgroundColor: "#dcfce7",
                                        border: "1px solid #86efac",
                                        borderRadius: "4px",
                                        padding: "6px 12px",
                                        fontSize: "0.9em"
                                      }}>
                                        <span>{group.GroupName}</span>
                                        <button
                                          onClick={() => removeUserFromGroup(user.eMail, group.GroupName)}
                                          disabled={saving}
                                          style={{
                                            background: "none",
                                            border: "none",
                                            color: "#16a34a",
                                            cursor: saving ? "not-allowed" : "pointer",
                                            fontSize: "1.1em",
                                            padding: "0 4px",
                                          }}
                                          title="Remove from group"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p style={{ fontSize: "0.9em", color: "#6b7280", marginBottom: "12px" }}>No groups assigned</p>
                                )}
                              </div>

                              <div style={{ marginBottom: "16px" }}>
                                <p style={{ fontSize: "0.9em", fontWeight: "500", marginBottom: "8px" }}>Available Groups:</p>
                                {availableGroups.length > 0 ? (
                                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                    {availableGroups.map(group => {
                                      const isAssigned = userGroups.some(g => g.GroupName === group.name);
                                      return (
                                        <button
                                          key={group.name}
                                          onClick={() => addUserToGroup(user.eMail, group.name)}
                                          disabled={saving || isAssigned}
                                          style={{
                                            padding: "6px 12px",
                                            borderRadius: "4px",
                                            border: "1px solid #d1d5db",
                                            backgroundColor: isAssigned ? "#e5e7eb" : "#f3f4f6",
                                            color: isAssigned ? "#9ca3af" : "#374151",
                                            cursor: isAssigned || saving ? "not-allowed" : "pointer",
                                            fontSize: "0.9em",
                                            fontWeight: "500",
                                          }}
                                          title={isAssigned ? "Already assigned" : "Add to group"}
                                        >
                                          {isAssigned ? "✓ " : "+ "}{group.name}
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p style={{ fontSize: "0.9em", color: "#6b7280" }}>No groups available</p>
                                )}
                              </div>

                              <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "12px", marginTop: "12px" }}>
                                <p style={{ fontSize: "0.9em", fontWeight: "500", marginBottom: "8px" }}>Create New Group:</p>
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <input
                                    type="text"
                                    placeholder="Group name"
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    onKeyPress={(e) => e.key === "Enter" && createNewGroup()}
                                    disabled={saving}
                                    style={{
                                      padding: "6px 12px",
                                      borderRadius: "4px",
                                      border: "1px solid #d1d5db",
                                      fontSize: "0.9em",
                                      flex: 1,
                                    }}
                                  />
                                  <Button
                                    size="small"
                                    onClick={createNewGroup}
                                    isLoading={saving}
                                    variation="primary"
                                  >
                                    Create
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={isPrivileged ? 3 : 2} style={{ textAlign: "center", color: "#999" }}>
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          {/* Stock Tab */}
          {/* Dashboard Cards */}
          <div className="dashboard-cards">
            <div className="card">
              <div className="card-value">{stats.total}</div>
              <div className="card-label">Total Items</div>
            </div>

            <div className="card card-low">
              <div className="card-value">{stats.low}</div>
              <div className="card-label">Low Stock</div>
            </div>
                        <div className="card card-critical">
              <div className="card-value">{stats.critical}</div>
              <div className="card-label">Critical Stock</div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="search-section">
            <input
              type="text"
              placeholder="Search by Tool Number, Part Number, or Location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-section">
            {isPrivileged && availableCustomers.length > 0 && (
              <div className="filter-group">
                <label>Customer:</label>
                <select 
                  value={selectedCustomerId || ""} 
                  onChange={(e) => setSelectedCustomerId(e.target.value)}
                  className="filter-select"
                >
                  {availableCustomers.map((customerId) => (
                    <option key={customerId} value={customerId}>
                      {customerId}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="filter-group">
              <label>Status:</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
                <option value="all">All Status</option>
                <option value="good">🟢 Good</option>
                <option value="low">🟡 Low</option>
                <option value="critical">🔴 Critical</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Live:</label>
              <select value={liveFilter} onChange={(e) => setLiveFilter(e.target.value)} className="filter-select">
                <option value="all">All Items</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Sort:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
                <option value="toolNumber">Tool Number</option>
                <option value="status">Status</option>
                <option value="stock">Stock Level</option>
              </select>
            </div>

            <button 
              className="export-button"
              onClick={exportToCSV}
              title="Download as CSV"
            >
              📥 Export
            </button>

            {isPrivileged && (
              <button 
                className="export-button"
                onClick={() => setShowAddMaterialModal(true)}
                title="Add new material"
              >
                ➕ Add Material
              </button>
            )}

            {isPrivileged && (
              <>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".htm,.html"
                  style={{ display: "none" }}
                  onChange={handleImportFileChange}
                />
                <button
                  className="export-button"
                  onClick={() => importFileRef.current && importFileRef.current.click()}
                  title="Import orders from SAP HTML report"
                >
                  📤 Import Orders
                </button>
              </>
            )}
          </div>

          {isPrivileged && showAddMaterialModal && (
            <div style={{
              backgroundColor: "rgba(0,0,0,0.5)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "8px",
                maxWidth: "500px",
                maxHeight: "80vh",
                overflowY: "auto"
              }}>
                <h2 style={{ marginTop: 0 }}>Add New Material</h2>
                
                <div style={{ display: "grid", gap: "12px", marginBottom: "12px" }}>
                  <input
                    type="text"
                    placeholder="Tool Number *"
                    value={newMaterial.ToolNumber}
                    onChange={(e) => setNewMaterial({...newMaterial, ToolNumber: e.target.value})}
                    style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                  <input
                    type="text"
                    placeholder="Customer Part Number"
                    value={newMaterial.CustomerPartNumber}
                    onChange={(e) => setNewMaterial({...newMaterial, CustomerPartNumber: e.target.value})}
                    style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                  <input
                    type="text"
                    placeholder="Storage Location"
                    value={newMaterial.StorageLocation}
                    onChange={(e) => setNewMaterial({...newMaterial, StorageLocation: e.target.value})}
                    style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                  <input
                    type="text"
                    placeholder="Specification"
                    value={newMaterial.Specification}
                    onChange={(e) => setNewMaterial({...newMaterial, Specification: e.target.value})}
                    style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                  <input
                    type="text"
                    placeholder="Shape"
                    value={newMaterial.Shape}
                    onChange={(e) => setNewMaterial({...newMaterial, Shape: e.target.value})}
                    style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                  <input
                    type="text"
                    placeholder="Dimensions"
                    value={newMaterial.Dim_Value}
                    onChange={(e) => setNewMaterial({...newMaterial, Dim_Value: e.target.value})}
                    style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                  <select
                    value={newMaterial.materialCustomerId}
                    onChange={(e) => setNewMaterial({...newMaterial, materialCustomerId: e.target.value})}
                    style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                  >
                    <option value="">Select Customer ID *</option>
                    {availableCustomers.map(customerId => (
                      <option key={customerId} value={customerId}>{customerId}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Shelf Level"
                    value={newMaterial.Shelf_Level_Actual}
                    onChange={(e) => setNewMaterial({...newMaterial, Shelf_Level_Actual: e.target.value})}
                    style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                  <input
                    type="number"
                    placeholder="Target Level"
                    value={newMaterial.Shelf_Level_Target}
                    onChange={(e) => setNewMaterial({...newMaterial, Shelf_Level_Target: e.target.value})}
                    style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                </div>
                
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <Button 
                    onClick={() => setShowAddMaterialModal(false)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={addMaterial}
                    isLoading={saving}
                    variation="primary"
                  >
                    Add Material
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="table-container">
            <table className="stock-table">
            <thead>
              <tr>
                <th>Tool Number</th>
                <th>Customer Number</th>
                <th>Location</th>
                <th>Specification</th>
                <th>Shape</th>
                <th>Dimensions</th>
                <th>Shelf Level</th>
                <th>Stock Target</th>
                <th>On Order</th>
                <th>Stock AO {lastFridayFormatted} 17:00</th>
                <th>Status</th>
                {isPrivileged && <th>Actions</th>}
              </tr>
            </thead>

            <tbody>
              {filteredMaterials.length > 0 ? (
                filteredMaterials.map((material) => {
                  const isEditing = editingId === material.id;

                  return (
                    <tr
                      key={material.id}
                      className={
                        !Boolean(!material.Live) === false
                          ? "inactive-row"
                          : "live-row"
                      }
                    >
                      {/* ToolNumber stays non-editable */}
                      <td>
                        <a
                          href={`http://abrasiveconstock.com/products/Rack_1/AcmeLtd/${sanitizeToolNumber(
                            material.ToolNumber
                          )}`}
                          className="toolnumber-link"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {material.ToolNumber}
                        </a>
                      </td>

                      <td>{material.CustomerPartNumber}</td>
                      <td>{material.StorageLocation}</td>
                      <td>{material.Specification}</td>
                      <td>{material.Shape}</td>
                      <td>{material.Dim_Value}</td>

                      {renderEditableIntCell(material, "Shelf_Level_Actual")}
                      {renderEditableIntCell(material, "Shelf_Level_Target")}
                      {renderOnOrderCell(material)}
                      {renderEditableIntCell(material, "LW_Stock")}

                      <td>{renderStatusBadge(material)}</td>

                      {isPrivileged && (
                        <td>
                          {!isEditing ? (
                            <div className="action-buttons-container">
                              <Button size="small" onClick={() => startEdit(material)} disabled={saving}>
                                Edit
                              </Button>
                              <Button 
                                size="small" 
                                onClick={() => toggleLiveStatus(material)}
                                disabled={saving}
                                title={material.Live ? "Deactivate" : "Activate"}
                                className={`btn-live-status ${material.Live ? "active" : "inactive"}`}
                              >
                                {material.Live ? "Live" : "Off"}
                              </Button>
                              <Button 
                                size="small" 
                                onClick={() => {
                                  if (window.confirm("Are you sure you want to delete this material?")) {
                                    removeMaterial(material.id);
                                  }
                                }}
                                disabled={saving}
                                className="btn-delete"
                              >
                                Delete
                              </Button>
                            </div>
                          ) : (
                            <div className="action-buttons-container editing">
                              <Button
                                size="small"
                                variation="primary"
                                isLoading={saving}
                                onClick={() => saveEdit(material)}
                              >
                                Update
                              </Button>
                              <Button
                                size="small"
                                variation="link"
                                onClick={cancelEdit}
                                disabled={saving}
                              >
                                Cancel
                              </Button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={isPrivileged ? 12 : 11} className="no-data">
                    No materials found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>


        </>
      )}
      </div>

      {/* Bottom Navigation Buttons */}
      <div className="button-group">
        {isPrivileged && (
          <div style={{ display: "flex", gap: "12px" }}>
            <Button 
              onClick={() => {
                setShowUsersTab(false);
                setShowUpdateOrdersTab(false);
              }}
              className={`tab-btn-bottom ${!showUsersTab && !showUpdateOrdersTab ? "tab-btn-bottom-active" : ""}`}
            >
              📦 Stock
            </Button>
            <Button 
              onClick={() => {
                setShowUsersTab(false);
                setShowUpdateOrdersTab(true);
              }}
              className={`tab-btn-bottom ${showUpdateOrdersTab ? "tab-btn-bottom-active" : ""}`}
            >
              📋 Orders
            </Button>
            <Button 
              onClick={() => {
                setShowUsersTab(true);
                setShowUpdateOrdersTab(false);
              }}
              className={`tab-btn-bottom ${showUsersTab ? "tab-btn-bottom-active" : ""}`}
            >
              👥 Users
            </Button>
          </div>
        )}
        {isPrivileged && (
          <Button 
            className="export-button"
            onClick={() => navigate("/updateonorder")}
            title="Go to update orders page"
          >
            📬 Update On Order
          </Button>
        )}
        <Button className="go-back-button" onClick={() => navigate(-1)}>
          Back
        </Button>
        <Button 
          onClick={signOut}
          className="go-back-button"
        >
          Sign Out
        </Button>
        {/* Import Orders Preview Modal */}
        {showImportModal && (
          <div
            style={{
              backgroundColor: "rgba(0,0,0,0.5)",
              position: "fixed",
              top: 0, left: 0, right: 0, bottom: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "8px",
                maxWidth: "700px",
                width: "90%",
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              <h2 style={{ marginTop: 0 }}>Import Orders Preview</h2>

              {importWarnings.length > 0 && (
                <div
                  style={{
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: "4px",
                    padding: "12px",
                    marginBottom: "16px",
                  }}
                >
                  <strong>⚠️ The following tool numbers were not found and will be skipped:</strong>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                    {importWarnings.map((w, i) => (
                      <li key={i} style={{ fontSize: "0.9em", color: "#856404" }}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p style={{ color: "#555", marginBottom: "16px" }}>
                This will <strong>delete all existing orders</strong> and create{" "}
                <strong>{importPreview.length}</strong> new order(s) from the uploaded file.
              </p>

              {importPreview.length > 0 ? (
                <div style={{ overflowX: "auto", marginBottom: "16px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f3f4f6", borderBottom: "2px solid #d1d5db" }}>
                        <th style={{ padding: "8px", textAlign: "left" }}>Tool Number</th>
                        <th style={{ padding: "8px", textAlign: "left" }}>Order #</th>
                        <th style={{ padding: "8px", textAlign: "left" }}>Qty</th>
                        <th style={{ padding: "8px", textAlign: "left" }}>Delivery Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((row, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #e5e7eb" }}>
                          <td style={{ padding: "8px" }}>{row.toolNumber}</td>
                          <td style={{ padding: "8px" }}>{row.orderNumber}</td>
                          <td style={{ padding: "8px" }}>{row.qty}</td>
                          <td style={{ padding: "8px" }}>{row.deliveryDate || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: "#999", fontStyle: "italic" }}>No valid orders to import.</p>
              )}

              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportPreview([]);
                    setImportWarnings([]);
                  }}
                  disabled={importing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={importOrders}
                  isLoading={importing}
                  variation="primary"
                  disabled={importing || importPreview.length === 0}
                >
                  Confirm Import
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* On Order Details Modal */}
        {selectedOrderMaterial && (
          <div className="modal-overlay" onClick={() => setSelectedOrderMaterial(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "700px", maxHeight: "80vh", overflowY: "auto" }}>
              <div className="modal-header">
                <h2>Orders for {selectedOrderMaterial.ToolNumber}</h2>
                <button 
                  className="modal-close-btn"
                  onClick={() => setSelectedOrderMaterial(null)}
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-body">
                <div className="order-detail-item">
                  <label>Customer:</label>
                  <span>{selectedOrderMaterial.materialCustomerId || "N/A"}</span>
                </div>

                <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                  <h3 style={{ margin: "0 0 15px 0", fontSize: "16px", fontWeight: "600" }}>Orders</h3>
                  
                  {orders.length > 0 ? (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                        <thead>
                          <tr style={{ borderBottom: "2px solid #667eea" }}>
                            <th style={{ padding: "8px", textAlign: "left", fontWeight: "600" }}>Order #</th>
                            <th style={{ padding: "8px", textAlign: "left", fontWeight: "600" }}>Qty</th>
                            <th style={{ padding: "8px", textAlign: "left", fontWeight: "600" }}>Expected Delivery</th>
                            {isPrivileged && <th style={{ padding: "8px", textAlign: "center", fontWeight: "600" }}>Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((order) => (
                            <tr key={order.id} style={{ borderBottom: "1px solid #e0e0e0" }}>
                              <td style={{ padding: "8px" }}>{order.OrderNumber || "-"}</td>
                              <td style={{ padding: "8px" }}>{order.Quantity || "-"}</td>
                              <td style={{ padding: "8px" }}>{order.ExpectedDeliveryDate ? new Date(order.ExpectedDeliveryDate).toLocaleDateString() : "-"}</td>
                              {isPrivileged && (
                                <td style={{ padding: "8px", textAlign: "center" }}>
                                  <Button 
                                    size="small" 
                                    onClick={() => removeOrder(order.id)}
                                    disabled={savingOrderPopup}
                                    className="btn-delete"
                                  >
                                    Remove
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p style={{ color: "#999", fontStyle: "italic" }}>No orders yet</p>
                  )}
                </div>

                {isPrivileged && (
                  <div style={{ marginTop: "20px", padding: "15px", backgroundColor: "#f9f9f9", borderRadius: "8px", border: "1px solid #e0e0e0" }}>
                    <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", fontWeight: "600" }}>Add New Order</h4>
                    <div style={{ display: "grid", gap: "10px" }}>
                      <input
                        type="text"
                        placeholder="Order Number"
                        value={newOrder.OrderNumber}
                        onChange={(e) => setNewOrder({ ...newOrder, OrderNumber: e.target.value })}
                        style={{ 
                          padding: "8px", 
                          fontSize: "14px", 
                          border: "1px solid #ddd", 
                          borderRadius: "4px",
                          width: "100%"
                        }}
                      />
                      <input
                        type="number"
                        placeholder="Quantity"
                        value={newOrder.Quantity}
                        onChange={(e) => setNewOrder({ ...newOrder, Quantity: e.target.value })}
                        style={{ 
                          padding: "8px", 
                          fontSize: "14px", 
                          border: "1px solid #ddd", 
                          borderRadius: "4px",
                          width: "100%"
                        }}
                      />
                      <input
                        type="date"
                        value={newOrder.ExpectedDeliveryDate}
                        onChange={(e) => setNewOrder({ ...newOrder, ExpectedDeliveryDate: e.target.value })}
                        style={{ 
                          padding: "8px", 
                          fontSize: "14px", 
                          border: "1px solid #ddd", 
                          borderRadius: "4px",
                          width: "100%"
                        }}
                      />
                      <Button 
                        onClick={() => addOrder()}
                        isLoading={savingOrderPopup}
                        variation="primary"
                      >
                        Add Order
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="modal-footer">
                <Button onClick={() => setSelectedOrderMaterial(null)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withAuthenticator(ViewAllStockPage, {
  signUpConfig: {
    hiddenDefaults: ["email", "password"],
  },
  hideSignUp: true,
});
